import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useUser } from '@fs/zion-user'
import { NoticeLoading } from '@fs/zion-icon'
import { useErrorHandlerOverlay, useLazyRequest } from '../../../hooks'
import { PillButton } from '../../../lib/buttons'
import { getContractUrl } from '../../../lib/helpers/contractHelpers'

interface AgreementButtonProps {
  sessionId: string
  locale: string
  userHasSigned: boolean
  disabled: boolean
}

export default function AgreementButton({
  sessionId,
  locale,
  userHasSigned,
  disabled,
}: AgreementButtonProps): JSX.Element {
  const [t] = useTranslation()
  const [postContract] = useLazyRequest(getContractUrl)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const user = useUser()
  const isMountedRef = useRef(true)
  useErrorHandlerOverlay(error)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const onClick = async (): Promise<void> => {
    setLoading(true)
    try {
      const path = await postContract({ sessionId, locale, cisId: user.cisId })
      // we need to use location.assign in order to leave the app and route via the blueprint
      window.location.assign(path)
      // NOTE: don't update the loading state because we want to keep the button disabled while the browser is loading the page
    } catch (e) {
      if (isMountedRef.current) {
        setError(e as Error)
        setLoading(false)
      }
    }
  }

  return (
    <PillButton
      emphasis="medium"
      linkName="Sign Contract"
      data-testid="button-sign-contract"
      onClick={onClick}
      disabled={loading || disabled || userHasSigned}
      Icon={loading ? NoticeLoading : undefined}
    >
      {userHasSigned ? t('dashboard.signed-contract', 'Signed') : t('dashboard.sign-contract', 'Sign Contract')}
    </PillButton>
  )
}
